import { useState } from 'react';
import { Grid, makeStyles, Theme, useTheme } from '@material-ui/core';
import SingleStat from './SingleStat';
import StatisticOverview from './StatisticOverview';
import Modal from '../shared/Modal';
import LineChart from '../charts/LineChart';
import DoughnutChart from '../charts/DoughnutChart';
import PieChart from '../charts/PieChart';
import BubbleChart from '../charts/BubbleChart';
import BarChart from '../charts/BarChart';
import CompetenceOverview from './CompetenceOverview';
import ReactLoading from 'react-loading';
import { IUsersStatistics } from 'utils/wemble-api.generated';
import { notEmpty } from 'utils/helpers';
import { useGetCurrentUserQuery, useGetCompetencesQuery } from 'utils/wemble-api';
import organization from 'utils/organization';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles(() => ({
  statistics: {
    marginTop: 30,
  },
  profile: {
    position: 'relative',
    height: 75,
    width: 75,
    borderRadius: '50%',
    border: 'solid 6px white',
    marginRight: 20,
    backgroundColor: '#efefef',
  },
  profile__img: {
    width: '100%',
    overflow: 'hidden',
    borderRadius: '50%',
    verticalAlign: 'unset',
    fontSize: 12,
  },
  profile__initials: {
    backgroundColor: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    borderRadius: '50%',
    fontSize: 22,
    fontWeight: 600,
  },
  loader: {
    fill: '#59ABCB',
    margin: 'auto',
    marginTop: 50,
    width: '12%',
  },
}));

function workloadToStatus(workload) {
  return Math.floor(Math.round(workload * 10) / 10 + 0.5);
}

function colorForWorkload(workload, theme) {
  return statusBackgroundColor(theme, workloadToStatus(workload))
}

function getInitials(name) {
  const nameSplit = name.toUpperCase().split(' ');
  const nameLength = nameSplit.length;
  if (nameLength > 1) {
    return [nameSplit[0].substring(0, 1), nameSplit[nameLength - 1].substring(0, 1)];
  } else if (nameLength === 1) {
    return [nameSplit[0].substring(0, 1), ''];
  } else return '';
}

function statusBackgroundColor(theme: Theme, status: number) {
  switch (status) {
    case 0:
      return theme.palette.statusCards.blue;
    case 1:
      return theme.palette.statusCards.green;
    case 2:
      return theme.palette.statusCards.yellow;
    case 3:
      return theme.palette.statusCards.red;
    default:
      return theme.palette.statusCards.gray;
  }
}

function statusColor(theme: Theme, status: number | null) {
  switch (status) {
    case 0:
      return theme.palette.blue.b2;
    case 1:
      return theme.palette.green.g4;
    case 2:
      return theme.palette.yellow.y2;
    case 3:
      return theme.palette.red.r2;
    default:
      return '#666';
  }
}

export type StatisticLayout = 'admin' | 'user';

const StatisticsGraphs = ({
  layout,
  statistics,
  showTeams,
}: {
  layout: StatisticLayout | undefined;
  statistics: IUsersStatistics | null;
  showTeams: boolean;
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const [visible, setVisible] = useState(false);
  const [modalSegment, setModalSegment] = useState<false | Number>(false);
  const [competenceModalVisible, setCompetenceModalVisible] = useState(false);
  const { data: currentUser } = useGetCurrentUserQuery();
  const { data: allCompetences } = useGetCompetencesQuery();
  const isWFHDisabled = organization(currentUser?.company).disableWFH;

  const toggleModal = () => {
    setModalSegment(false);
    setVisible(!(visible || Boolean(modalSegment)));

  };

  const toggleCompetenceModal = () => {
    setCompetenceModalVisible(!competenceModalVisible);
  };

  if (!statistics || !layout) {
    return <ReactLoading type={'spinningBubbles'} className={classes.loader} />;
  }

  const seriouslyBusyMoreThan80percentOfTime =
    (100 *
      Object.values(statistics.userWorkloadData).filter((user) => (user.portionOfSeriouslyBusy ?? 0) >= 0.8).length) /
    Object.values(statistics.userWorkloadData).length;
  const availableMoreThan80percentOfTime =
    (100 * Object.values(statistics.userWorkloadData).filter((user) => (user.portionOfAvailable ?? 0) >= 0.8).length) /
    Object.values(statistics.userWorkloadData).length;

  return (
    <div className={classes.statistics}>
      <Grid container>
        {statistics.averageWorkloadOverTime && (
          <Grid
            item
            xs={12}
            md={layout !== 'user' ? 7 : 9}
            style={{
              paddingRight: 20,
            }}
          >
            <LineChart
              key={'average-workload-line-chart'}
              data={statistics.averageWorkloadOverTime.map(([, v]) => v)}
              labels={statistics.averageWorkloadOverTime.map(([date]) => new Date(date).toISOString().split('T')[0])}
              title={'Average workload'}
            />
          </Grid>
        )}
        <Grid
          item
          xs={12}
          md={3}
          style={{
            paddingRight: 20,
          }}
        >
          <DoughnutChart
            data={Object.values(statistics.workloadDistributions)}
            title={'Workload distribution'}
            hideTooltip
            centerText={statistics.averageWorkload?.toFixed(1)}
            centerTextColor={statusColor(theme, Math.round(statistics.averageWorkload) - 1)}
            onclick={(workload) => setModalSegment(workload)}
          />
        </Grid>

        {layout !== 'user' && (
          <Grid container item xs={12} md={2}>
            <Grid
              item
              style={{
                width: '100%',
              }}
            >
              <SingleStat
                data={`${seriouslyBusyMoreThan80percentOfTime?.toFixed(0)}%`}
                text={`Of users seriously busy more than 80% of the time`}
                color={theme.palette.red.r1}
                backgroundColor={theme.palette.red.opacity.r16}
              />
            </Grid>
            <Grid
              item
              style={{
                marginTop: 20,
                width: '100%',
              }}
            >
              <SingleStat
                data={`${availableMoreThan80percentOfTime?.toFixed(0)}%`}
                text={`Of users available more than 80% of the time`}
                color={theme.palette.blue.b2}
                backgroundColor={theme.palette.blue.opacity.b16}
              />
            </Grid>
          </Grid>
        )}
      </Grid>

      {layout !== 'user' && (
        <Grid container style={{ marginTop: 20 }}>
          <Grid container item xs={12} md={2}>
            <Grid
              item
              style={{
                width: '100%',
              }}
            >
              <SingleStat
                data={
                  ((100 * (statistics.workloadForecast?.lower ?? 0)) / (statistics?.nbrOfUsers ?? 1))?.toFixed(0) + '%'
                }
                text={`Are forecasting a lower workload`}
                color={theme.palette.blue.b2}
                backgroundColor={theme.palette.blue.opacity.b16}
              />
            </Grid>
            <Grid
              item
              style={{
                marginTop: 20,
                width: '100%',
              }}
            >
              <SingleStat
                data={
                  ((100 * (statistics.workloadForecast?.higher ?? 0)) / (statistics?.nbrOfUsers ?? 1))?.toFixed(0) + '%'
                }
                text={`Are forecasting a higher workload`}
                color={theme.palette.red.r1}
                backgroundColor={theme.palette.red.opacity.r16}
              />
            </Grid>
          </Grid>
          <Grid item md={10} xs={12} style={{ paddingLeft: 20 }}>
            <StatisticOverview title={showTeams ? 'Teams overview' : 'People overview'} showMore={toggleModal}>
              {showTeams
                ? Object.entries(statistics.teamWorkload)
                  .filter((entry) => {
                    const [officeId, groupId] = entry[0].split('_');
                    const office = statistics.data.offices.find((o) => o._id === officeId);
                    const group = statistics.data.groups.find((g) => g._id === groupId);
                    return !(!office || !group);
                  })
                  .splice(0, 8)
                  .map(([id, data]) => {
                    const [officeId, groupId] = id.split('_');
                    const office = statistics.data.offices.find((o) => o._id === officeId);
                    const group = statistics.data.groups.find((g) => g._id === groupId);
                    if (!office || !group) return null;
                    return (
                      <Grid item md={3} xs={6} key={`team-stat-${officeId}-${groupId}`}>
                        <SingleStat
                          backgroundColor={colorForWorkload(data.avgWorkload, theme)}
                          color={statusColor(theme, workloadToStatus(data.avgWorkload ?? 0))}
                          data={
                            typeof data.avgWorkload === 'number' && !isNaN(data.avgWorkload)
                              ? (data.avgWorkload + 1).toFixed(1)
                              : ''
                          }
                          text={`${office?.name} ${group?.name}`}
                        />
                      </Grid>
                    );
                  })
                : statistics.data.users
                  .filter((user) => statistics.userWorkloadData[user._id!])
                  .splice(0, 8)
                  .map((user) => [user._id!, statistics.userWorkloadData[user._id!]] as [string, any])
                  .map(([id, data]) => {
                    const user = statistics.data.users.find((x) => x._id === id);
                    if (!user) return null;
                    return (
                      <Grid item md={3} xs={6} key={`users-stat-${id}`}>
                        <SingleStat
                          backgroundColor={colorForWorkload(data.avgWorkload, theme)}
                          color={statusColor(theme, workloadToStatus(data.avgWorkload ?? 0))}
                          data={
                            typeof data.avgWorkload === 'number' && !isNaN(data.avgWorkload)
                              ? (data.avgWorkload + 1).toFixed(1)
                              : ''
                          }
                          text={user?.name ?? ''}
                          ImageComponent={
                            <div className={classes.profile}>
                              {user?.profilePicture ? (
                                <img
                                  alt=""
                                  src={'https://d378wr66tg5qg7.cloudfront.net/' + user?.profilePicture}
                                  className={classes.profile__img}
                                />
                              ) : (
                                <div className={classes.profile__initials}>
                                  <span style={{ color: statusColor(theme, null) }}>
                                    {getInitials(user?.name)[0]}
                                  </span>
                                  <span style={{ color: statusColor(theme, null) }}>
                                    {getInitials(user?.name)[1]}
                                  </span>
                                </div>
                              )}
                            </div>
                          }
                        />
                      </Grid>
                    );
                  })}
            </StatisticOverview>
          </Grid>
        </Grid>
      )}

      {!isWFHDisabled && (
        <Grid container style={{ marginTop: 20 }}>
          {layout !== 'user' ? (
            <>
              <Grid container item xs={12} md={3}>
                <Grid
                  item
                  style={{
                    width: '100%',
                    paddingRight: 20,
                  }}
                >
                  <BubbleChart
                    data={statistics.WFHToWorkloadCorrelation.filter(notEmpty)}
                    title={'Workload'}
                    chartText="WFH"
                  />
                </Grid>
              </Grid>
              <Grid container item xs={12} md={3}>
                <Grid
                  item
                  style={{
                    width: '100%',
                    paddingRight: 20,
                  }}
                >
                  <PieChart
                    data={Object.values(statistics.nbrOfHomeDaysPerWeek)}
                    title={'Home-days per week'}
                    showDataLabels
                    dataLabelColors={['black', 'black', 'black', 'white', 'white', 'white']}
                    backgroundColor={[
                      'rgba(76, 162, 205, 0)',
                      'rgba(76, 162, 205, 0.2)',
                      'rgba(76, 162, 205, 0.4)',
                      'rgba(76, 162, 205, 0.6)',
                      'rgba(76, 162, 205, 0.8)',
                      'rgba(76, 162, 205, 1)',
                    ]}
                  />
                </Grid>
              </Grid>
            </>
          ) : null}
          {layout === 'user' && (
            <Grid container item xs={12} md={3}>
              <Grid
                item
                style={{
                  width: '100%',
                  paddingRight: 20,
                }}
              >
                <BarChart
                  data={Object.values(statistics.workFromHomePerWeekday ?? {}).map((v) => v * 100)}
                  title={'WFH per week-day'}
                />
              </Grid>
            </Grid>
          )}

          <Grid container item xs={12} md={layout !== 'user' ? 6 : 9}>
            <Grid
              item
              style={{
                width: '100%',
              }}
            >
              <LineChart
                key={'work-from-home-line-chart'}
                data={statistics.portionOfUsersWorkingFromHomeOverTime.map((x) => x.portionAtHome)}
                labels={statistics.portionOfUsersWorkingFromHomeOverTime.map((x) =>
                  x.date.split('T')[0],
                )}
                title={'Work from home'}
                yAxisTickFormatFn={(v) => `${v}%`}
              />
            </Grid>
          </Grid>
        </Grid>
      )}

      {layout !== 'user' && (
        <Grid container style={{ marginTop: 20 }}>
          <Grid container item xs={12} md={isWFHDisabled ? 6 : 4}>
            <Grid
              item
              style={{
                width: '100%',
                paddingRight: 20,
              }}
            >
              <CompetenceOverview
                data={statistics.competenceWorkload}
                competences={statistics.data.competences}
                users={statistics.data.users}
                showMore={toggleCompetenceModal}
              />
            </Grid>
          </Grid>
          {!isWFHDisabled && (
            <>
              <Grid container item xs={12} md={5}>
                <Grid container item xs={12} md={6} style={{ paddingRight: 20 }}>
                  <Grid
                    item
                    style={{
                      width: '100%',
                    }}
                  >
                    <SingleStat
                      data={`${(100 * (statistics?.percentOfMoreThan4daysHome ?? 0))?.toFixed(0)}%`}
                      text={`At home more than 4 days a week`}
                      color={theme.palette.blue.b3}
                      backgroundColor={'white'}
                    />
                  </Grid>
                  <Grid
                    item
                    style={{
                      marginTop: 20,
                      width: '100%',
                    }}
                  >
                    <SingleStat
                      data={`${(100 * (statistics?.percentOfMoreThan4daysAtOffice ?? 0))?.toFixed(0)}%`}
                      text={`At the office more than 4 days a week`}
                      color={theme.palette.blue.b2}
                      backgroundColor={'white'}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={12} md={6} style={{ paddingRight: 20 }}>
                  <Grid
                    item
                    style={{
                      width: '100%',
                    }}
                  >
                    <SingleStat
                      data={`${(100 * (statistics?.peakOfficeOccupancy ?? 0))?.toFixed(0)}%`}
                      text={`Peak office occupancy`}
                      color={theme.palette.yellow.y3}
                      backgroundColor={'white'}
                    />
                  </Grid>
                  <Grid
                    item
                    style={{
                      marginTop: 20,
                      width: '100%',
                    }}
                  >
                    <SingleStat
                      data={`${(100 * (statistics?.percentOnLeave ?? 0))?.toFixed(0)}%`}
                      text={`Average out of office`}
                      color={theme.palette.gray.g2}
                      backgroundColor={theme.palette.gray.g9}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid container item xs={12} md={3}>
                <Grid
                  item
                  style={{
                    width: '100%',
                  }}
                >
                  <BarChart
                    data={Object.values(statistics.workFromHomePerWeekday ?? {}).map((v) => v * 100)}
                    title={'WFH per week-day'}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      )}
      <Modal visible={visible || Boolean(modalSegment)} close={toggleModal} customWidth={1000}>
        <Grid container spacing={2}>
          {!Boolean(modalSegment) && showTeams
            ? Object.entries(statistics.teamWorkload)
              .filter((entry) => {
                const [officeId, groupId] = entry[0].split('_');
                const office = statistics.data.offices.find((o) => o._id === officeId);
                const group = statistics.data.groups.find((g) => g._id === groupId);
                return !(!office || !group);
              })
              .map(([id, data]) => {
                const [officeId, groupId] = id.split('_');
                const office = statistics.data.offices.find((o) => o._id === officeId);
                const group = statistics.data.groups.find((g) => g._id === groupId);
                if (!office || !group) return null;
                return (
                  <Grid item md={3} xs={6} key={`team-stat-${officeId}-${groupId}`}>
                    <SingleStat
                      backgroundColor={colorForWorkload(data.avgWorkload, theme)}
                      color={statusColor(theme, workloadToStatus(data.avgWorkload ?? 0))}
                      data={
                        typeof data.avgWorkload === 'number' && !isNaN(data.avgWorkload)
                          ? (data.avgWorkload + 1).toFixed(1)
                          : ''
                      }
                      text={`${office?.name} ${group?.name}`}
                    />
                  </Grid>
                );
              })
            : statistics.data.users
              .filter((user) => statistics.userWorkloadData[user._id!] && (!modalSegment || (colorForWorkload(statistics.userWorkloadData[user._id!].avgWorkload, theme) == colorForWorkload(Number(modalSegment) - 1, theme))))
              .map((user) => [user._id!, statistics.userWorkloadData[user._id!]] as [string, any])
              .map(([id, data]) => {
                const user = statistics.data.users.find((x) => x._id === id);
                if (!user) return null;
                return (
                  <Grid item md={3} xs={6} key={`users-stat-${id}`}>
                    <SingleStat
                      backgroundColor={colorForWorkload(data.avgWorkload, theme)}
                      color={statusColor(theme, workloadToStatus(data.avgWorkload ?? 0))}
                      data={
                        typeof data.avgWorkload === 'number' && !isNaN(data.avgWorkload)
                          ? (data.avgWorkload + 1).toFixed(1)
                          : ''
                      }
                      text={user?.name ?? ''}
                      ImageComponent={
                        <div className={classes.profile}>
                          {user?.profilePicture ? (
                            <img
                              alt=""
                              src={'https://d378wr66tg5qg7.cloudfront.net/' + user?.profilePicture}
                              className={classes.profile__img}
                            />
                          ) : (
                            <div className={classes.profile__initials}>
                              <span style={{ color: statusColor(theme, null) }}>{getInitials(user?.name)[0]}</span>
                              <span style={{ color: statusColor(theme, null) }}>{getInitials(user?.name)[1]}</span>
                            </div>
                          )}
                        </div>
                      }
                    />
                  </Grid>
                );
              })}
        </Grid>
      </Modal>
      <Modal visible={competenceModalVisible} close={toggleCompetenceModal} customWidth={1200}>
        <div style={{ padding: 20 }}>
          <h2 style={{ marginTop: 0, marginBottom: 20, fontSize: 24, fontWeight: 600 }}>All Competencies</h2>
          {(() => {
            if (!allCompetences) return null;

            // Group competences by their parent category, matching UniversalFilterSelect logic
            const groupedByCategory: Record<string, Array<[string, typeof statistics.competenceWorkload[string]]>> = {};

            // Find all competences that are parents (have children) from the full competence list
            const parentCompetences = allCompetences.filter(competence =>
              allCompetences.find(c => {
                const parentId = typeof c.parent === 'string' ? c.parent : c.parent?._id;
                return parentId === competence._id;
              })
            );

            // Create groups for each parent
            parentCompetences.forEach(parentComp => {
              // Determine the category label
              const parentOfParent = typeof parentComp.parent === 'string'
                ? allCompetences.find(c => c._id === parentComp.parent)
                : parentComp.parent;
              const categoryLabel = parentOfParent?.name
                ? `${parentOfParent.name} - ${parentComp.name}`
                : parentComp.name ?? 'Unknown';

              // Find all competences that have this parent and are in the workload data
              const competencesInCategory: Array<[string, typeof statistics.competenceWorkload[string]]> = [];
              Object.entries(statistics.competenceWorkload).forEach(([competenceId, data]) => {
                const competence = allCompetences.find((c) => c._id === competenceId);
                if (!competence) return;

                const parentId = typeof competence.parent === 'string' ? competence.parent : competence.parent?._id;
                if (parentId === parentComp._id && !competence.root) {
                  competencesInCategory.push([competenceId, data]);
                }
              });

              if (competencesInCategory.length > 0) {
                groupedByCategory[categoryLabel] = competencesInCategory;
              }
            });

            // Handle standalone competences (no parent and not root)
            const standaloneCompetences: Array<[string, typeof statistics.competenceWorkload[string]]> = [];
            Object.entries(statistics.competenceWorkload).forEach(([competenceId, data]) => {
              const competence = allCompetences.find((c) => c._id === competenceId);
              if (!competence) return;

              if (!competence.root && !competence.parent) {
                standaloneCompetences.push([competenceId, data]);
              }
            });

            if (standaloneCompetences.length > 0) {
              groupedByCategory['Competencies'] = standaloneCompetences;
            }

            return (
              <>
                {Object.entries(groupedByCategory)
                  .sort(([a], [b]) => {
                    // Sort "Competencies" to the end
                    if (a === 'Competencies') return 1;
                    if (b === 'Competencies') return -1;
                    return a.localeCompare(b);
                  })
                  .map(([categoryName, competences]) => (
                    <div key={categoryName} style={{ marginBottom: 30 }}>
                      <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 15, color: theme.palette.gray.g1 }}>
                        {categoryName}
                      </h3>
                      <Grid container spacing={2}>
                        {competences
                          .sort(([, a], [, b]) => (b?.nbrOfUsers ?? 0) - (a?.nbrOfUsers ?? 0))
                          .map(([competenceId, data]) => {
                            const competence = allCompetences.find((c) => c._id === competenceId);
                            // Get users with this competence
                            const usersWithCompetence = statistics.data.users.filter(user =>
                              user.skills?.some(skill =>
                                typeof skill !== 'string' &&
                                skill.competence &&
                                (typeof skill.competence === 'string' ? skill.competence : skill.competence._id) === competenceId
                              )
                            );
                            const displayUsers = usersWithCompetence.slice(0, 3);
                            const remainingCount = usersWithCompetence.length - displayUsers.length;

                            return (
                              <Grid item xs={12} sm={6} md={4} lg={3} key={`competence-modal-${competenceId}`}>
                                <div
                                  style={{
                                    backgroundColor: statusBackgroundColor(theme, workloadToStatus(data.avgWorkload ?? 0)),
                                    borderRadius: 8,
                                    padding: 12,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 8,
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div
                                      style={{
                                        fontSize: 14,
                                        fontWeight: 500,
                                        flex: 1,
                                        marginRight: 10,
                                        cursor: 'pointer',
                                      }}
                                      onClick={() => history.push(`/competences/${competenceId}`)}
                                    >
                                      {competence?.name}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 24,
                                        fontWeight: 700,
                                        color: statusColor(theme, workloadToStatus(data.avgWorkload ?? 0)),
                                      }}
                                    >
                                      {data.avgWorkload && !isNaN(data.avgWorkload)
                                        ? (data.avgWorkload + 1).toFixed(1)
                                        : '-'}
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    {displayUsers.map((user, idx) => (
                                      <div
                                        key={user._id}
                                        style={{
                                          width: 28,
                                          height: 28,
                                          borderRadius: '50%',
                                          backgroundColor: '#efefef',
                                          border: '2px solid white',
                                          marginLeft: idx > 0 ? -8 : 0,
                                          overflow: 'hidden',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: 10,
                                          fontWeight: 600,
                                        }}
                                      >
                                        {user.profilePicture ? (
                                          <img
                                            alt=""
                                            src={
                                              'https://d378wr66tg5qg7.cloudfront.net/' + user.profilePicture
                                            }
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                          />
                                        ) : (
                                          <span>
                                            {getInitials(user.name)[0]}
                                            {getInitials(user.name)[1]}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                    {remainingCount > 0 && (
                                      <div
                                        style={{
                                          width: 28,
                                          height: 28,
                                          borderRadius: '50%',
                                          backgroundColor: theme.palette.gray.g6,
                                          border: '2px solid white',
                                          marginLeft: -8,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: 10,
                                          fontWeight: 600,
                                          color: theme.palette.gray.g2,
                                        }}
                                      >
                                        +{remainingCount}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </Grid>
                            );
                          })}
                      </Grid>
                    </div>
                  ))}
              </>
            );
          })()}
        </div>
      </Modal>
    </div>
  );
};
export default StatisticsGraphs;
